import { useEffect, useRef } from 'react';

/**
 * Custom hook that scrolls to a table element when pagination changes
 * @param {number} pageIndex - Current page index from pagination state
 * @param {string} tableSelector - CSS selector for the table element (e.g., '.events-table')
 * @param {number} offsetTop - Offset from top of an element (default: 100)
 * @param {number} delay - Delay before scrolling in ms (default: 100)
 * @param {boolean} shouldRestoreFocus - Whether to restore focus after scrolling (default: false)
 */
export const useScrollToPagination = (pageIndex, tableSelector, offsetTop = 100, delay = 100, shouldRestoreFocus = false) => {
  const isFirstRender = useRef(true);
  const previousPageIndex = useRef(pageIndex);

  useEffect(() => {
    // Skip scrolling on the first render, or if the page index hasn't actually changed
    if (isFirstRender.current || previousPageIndex.current === pageIndex) {
      isFirstRender.current = false;
      previousPageIndex.current = pageIndex;
      return;
    }

    previousPageIndex.current = pageIndex;

    const timer = setTimeout(() => {
      const element = document.querySelector(tableSelector);
      if (element) {
        const tableTopOffset = element.offsetTop;
        const adjustedOffset = tableTopOffset - offsetTop;
        window.scrollTo({
          top: Math.max(0, adjustedOffset),
          behavior: 'smooth',
        });
        
        // After scrolling, set focus to the first focusable blog item for keyboard navigation
        if (shouldRestoreFocus) {
          setTimeout(() => {
            const firstFocusableElement = element.querySelector('a[href], button:not([disabled])');
            if (firstFocusableElement) {
              firstFocusableElement.focus();
            }
          }, 300); // Wait for a smooth scroll to complete
        }
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [pageIndex, tableSelector, offsetTop, delay, shouldRestoreFocus]);
};
